"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { User } from "lucide-react"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { signout } from "@lib/data/customer"

const CLOSE_DELAY_MS = 200

export default function AccountPopover({
  customer,
}: {
  customer: HttpTypes.StoreCustomer | null
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [canHover, setCanHover] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Hover-capable devices (mouse/trackpad) get hover-open behavior. Touch
  // devices fall back to click-only — mobile browsers fire a synthetic
  // mouseenter/mouseleave pair on tap, which would otherwise flash the
  // popover open then immediately closed.
  useEffect(() => {
    setCanHover(window.matchMedia("(hover: hover)").matches)
  }, [])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const open = useCallback(() => {
    cancelClose()
    setIsOpen(true)
  }, [cancelClose])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)
  }, [cancelClose])

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [isOpen])

  useEffect(() => cancelClose, [cancelClose])

  const handleSignOut = async () => {
    setIsOpen(false)
    await signout("gb")
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={canHover ? open : undefined}
      onMouseLeave={canHover ? scheduleClose : undefined}
    >
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 text-gray-900 hover:opacity-80 px-2 py-1.5 rounded transition-opacity duration-200"
        aria-expanded={isOpen}
        aria-haspopup="true"
        data-testid="nav-account-link"
      >
        <User className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm">{customer ? "Account" : "Login"}</span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-[100000]"
          data-testid="account-popover-panel"
        >
          {!customer && (
            <>
              <LocalizedClientLink
                href="/account"
                className="block px-4 py-2.5 hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                <span className="text-sm font-semibold text-gray-900">Sign In ›</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  Access your account and order history
                </p>
              </LocalizedClientLink>
              <LocalizedClientLink
                href="/account?view=register"
                className="block px-4 py-2.5 hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                <span className="text-sm font-semibold text-gray-900">
                  Create an Account ›
                </span>
                <p className="text-xs text-gray-500 mt-0.5">
                  Save your details for faster checkout
                </p>
              </LocalizedClientLink>
              <div className="border-t border-gray-100 my-1" />
            </>
          )}

          {/*
            No "My Reviews" link here: /account/reviews doesn't exist as a
            page in this codebase (no reviews feature). Add it back only
            once that page actually exists.
          */}
          <LocalizedClientLink
            href="/account"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setIsOpen(false)}
          >
            My Account
          </LocalizedClientLink>
          <LocalizedClientLink
            href="/account/orders"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setIsOpen(false)}
          >
            My Orders
          </LocalizedClientLink>
          <LocalizedClientLink
            href="/account/profile"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setIsOpen(false)}
          >
            My Profile
          </LocalizedClientLink>
          <LocalizedClientLink
            href="/contact"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setIsOpen(false)}
          >
            Help
          </LocalizedClientLink>

          {customer && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Sign Out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
